import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, Typography, Paper, Alert, IconButton, Tooltip, 
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { format, addDays, subDays, parse, isBefore, addHours, differenceInMinutes, getDay, isSameDay } from 'date-fns';
import { es } from 'date-fns/locale';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

import { makeRequest } from '../../../assets/functions_helper';

function ReservationSchedule({ restaurantId }) {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [selectedReservation, setSelectedReservation] = useState(null);
  
  // Data local
  const [operatingHours, setOperatingHours] = useState(null);
  const [slotDuration, setSlotDuration] = useState(4); // default 4 hours
  const [tables, setTables] = useState([]);
  
  // Firestore data
  const [confirmedSchedules, setConfirmedSchedules] = useState([]);

  const fetchConfig = useCallback(async () => {
    if (!selectedDate || !restaurantId) return;
    setLoading(true);
    try {
      const restaurantPublicId = localStorage.getItem('restaurant_public_id') || restaurantId;
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      
      const [hoursRes, tablesRes, reservationsData] = await Promise.all([
        makeRequest('GET', `reservations/operating-hours?restaurant_public_id=${restaurantPublicId}`, null, 'application/json', null, null, null, true),
        makeRequest('GET', `restaurants/${restaurantId}/tables?can_be_reserved=true`, null, 'application/json', localStorage.getItem('token'), localStorage.getItem('tokenType'), null, true),
        // Solo traemos las confirmadas para este día, como optimizamos anteriormente
        makeRequest('GET', `reservations?restaurant_public_id=${restaurantPublicId}&status=confirmed&date=${dateStr}&limit=200`, null, 'application/json', localStorage.getItem('token'), localStorage.getItem('tokenType'), null, true)
      ]);

      setOperatingHours(hoursRes || {});
      if (hoursRes && hoursRes.slot_duration_hours) {
        setSlotDuration(hoursRes.slot_duration_hours);
      }
      setTables(tablesRes || []);

      const allRes = Array.isArray(reservationsData) ? reservationsData : (reservationsData?.results || []);

      // Map to be compatible with confirmed schedules rendering logic
      const confirmed = allRes.map(r => ({
        ...r,
        start_time: r.requested_datetime,
        table_ids: r.assigned_table_ids || [],
        date: r.requested_datetime ? r.requested_datetime.split('T')[0] : null
      }));
      setConfirmedSchedules(confirmed);

    } catch(err) {
      console.error("Error fetching dependencies for date:", err);
    } finally {
      setLoading(false);
    }
  }, [restaurantId, selectedDate]);

  useEffect(() => {
    fetchConfig();

    const handleReload = (e) => {
      if (!e.detail?.restaurantId || e.detail.restaurantId === restaurantId) {
        fetchConfig();
      }
    };

    window.addEventListener('reload_reservations', handleReload);
    return () => {
      window.removeEventListener('reload_reservations', handleReload);
    };
  }, [fetchConfig, restaurantId]);

  // Calcular Operación Diaria
  const dayIndexToKey = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayKey = dayIndexToKey[getDay(selectedDate)];
  const dayLimits = (operatingHours && operatingHours.operating_hours) ? operatingHours.operating_hours[dayKey] : (operatingHours ? operatingHours[dayKey] : null);

  const isClosed = !dayLimits || dayLimits.closed === true;

  // Render Engine (Matemática del Eje X)
  let openDate = null;
  let closeDate = null;
  let totalMinutes = 0;
  let hourMarks = [];

  if (!isClosed && dayLimits) {
    try {
      openDate = parse(dayLimits.open, 'HH:mm', selectedDate);
      closeDate = parse(dayLimits.close, 'HH:mm', selectedDate);
      if (isBefore(closeDate, openDate)) {
        closeDate = addDays(closeDate, 1);
      }
      totalMinutes = differenceInMinutes(closeDate, openDate);

      // Generar Marcas de Hora
      const totalHours = Math.ceil(totalMinutes / 60);
      for (let i = 0; i <= totalHours; i++) {
        hourMarks.push(addHours(openDate, i));
      }
    } catch(err) {
      console.error(err);
    }
  }

  // Helper para posicionar un bloque (dinámico según slotDuration del restaurante)
  const calculateBlockStyles = (timeStr) => {
    if (!openDate || !timeStr || totalMinutes === 0) return { display: 'none' };
    
    // Parse timeStr assuming "HH:mm" or "YYYY-MM-DD HH:mm:ss" o ISO-8601
    let reqDate;
    if (timeStr.includes('T')) {
       reqDate = new Date(timeStr);
    } else if (timeStr.includes(' ')) {
       reqDate = parse(timeStr, 'yyyy-MM-dd HH:mm:ss', new Date());
    } else {
       reqDate = parse(timeStr, 'HH:mm', selectedDate);
    }

    // Asegurar que reqDate corresponda al mismo día si cruzó la noche
    if (isBefore(reqDate, openDate) && differenceInMinutes(openDate, reqDate) > 720) {
       reqDate = addDays(reqDate, 1);
    }

    const diffMins = differenceInMinutes(reqDate, openDate);
    let leftPct = (diffMins / totalMinutes) * 100;
    
    // slotDuration dinamico * 60 minutos
    let widthPct = ((slotDuration * 60) / totalMinutes) * 100;

    // Clipping visual para no salirse de la caja derecha (Cierre) ni izquierda
    leftPct = Math.max(0, leftPct);
    if (leftPct + widthPct > 100) {
      widthPct = 100 - leftPct;
    }

    // Si empezó antes de abrir, clip inicio
    if (diffMins < 0) {
       leftPct = 0;
       widthPct = (((slotDuration * 60) + diffMins) / totalMinutes) * 100; 
       if (widthPct <= 0) return { display: 'none' };
    }

    // Si empezó después de cerrar, ocultar
    if (diffMins >= totalMinutes) return { display: 'none' };

    return {
      left: `${leftPct}%`,
      width: `${widthPct}%`,
      position: 'absolute',
      height: '80%',
      top: '10%',
      borderRadius: '8px',
      cursor: 'pointer',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      display: 'flex',
      alignItems: 'center',
      padding: '0 8px',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      fontSize: '0.8rem',
      fontWeight: 'bold',
      color: 'white',
      zIndex: 10,
      transition: 'transform 0.2s',
      '&:hover': { transform: 'scaleY(1.05)' }
    };
  };

  // Los schedules traen asignaciones reales.
  const confirmedForDay = confirmedSchedules.filter(s => {
    if (!s.start_time) return false;
    let sd;
    if (s.start_time.includes('T')) sd = new Date(s.start_time);
    else if (s.start_time.includes(' ')) sd = parse(s.start_time, 'yyyy-MM-dd HH:mm:ss', new Date());
    else sd = parse(s.start_time, 'HH:mm', selectedDate); 
    
    if (s.date) return s.date === format(selectedDate, 'yyyy-MM-dd');
    return isSameDay(sd, selectedDate);
  });

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, width: '100%', maxWidth: '1200px', margin: '0 auto' }}>
      
      {/* Controles y Título */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, color: '#0f172a', fontFamily: 'Como, sans-serif' }}>
            Agenda Visual (Gantt)
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            Visualización de ocupación. Cada reserva bloquea la mesa por el tiempo configurado para este restaurante ({slotDuration} hr{slotDuration !== 1 ? 's' : ''}).
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: { xs: 2, md: 0 } }}>
          <IconButton onClick={() => setSelectedDate(prev => subDays(prev, 1))} sx={{ bgcolor: '#f1f5f9' }}>
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <DatePicker
              value={selectedDate}
              onChange={(n) => n && setSelectedDate(n)}
              slotProps={{ textField: { size: 'small', sx: { width: 140, bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: '12px' } } } }}
            />
          </LocalizationProvider>
          <IconButton onClick={() => setSelectedDate(prev => addDays(prev, 1))} sx={{ bgcolor: '#f1f5f9' }}>
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Cuadrícula Principal */}
      <Paper elevation={0} sx={{ p: 0, borderRadius: '20px', border: '1px solid #e2e8f0', bgcolor: '#ffffff', overflow: 'hidden' }}>
        
        {loading ? null : isClosed ? (
          <Box p={4}>
            <Alert severity="error" sx={{ borderRadius: '12px', fontWeight: 700 }}>
              El restaurante está cerrado el {format(selectedDate, "eeee d 'de' MMMM", { locale: es })}.
            </Alert>
          </Box>
        ) : (
          <Box sx={{ minWidth: 800, overflowX: 'auto' }}>
            
            {/* Header del Eje X (Horas) */}
            <Box sx={{ display: 'flex', borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
              <Box sx={{ width: 200, minWidth: 200, maxWidth: 200, flexShrink: 0, p: 2, borderRight: '1px solid #e2e8f0', fontWeight: 800, color: '#475569' }}>
                Mesas / Horarios
              </Box>
              <Box sx={{ flex: 1, position: 'relative', display: 'flex' }}>
                {hourMarks.map((hm, i) => {
                   const left = (differenceInMinutes(hm, openDate) / totalMinutes) * 100;
                   if (left < 0 || left > 100) return null;
                   return (
                     <Box key={i} sx={{ position: 'absolute', left: `${left}%`, top: 0, bottom: 0, borderLeft: '1px dashed #cbd5e1', height: '100%', px: 1, pt: 2 }}>
                       <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>{format(hm, 'HH:mm')}</Typography>
                     </Box>
                   );
                })}
              </Box>
            </Box>

            {/* Filas: Mesas */}
            {tables.map(table => {
              // Buscar confirmados para esta mesa
              const tableEvents = confirmedForDay.filter(s => 
                 s.table_id === table.id || s.table_id === table.public_id || 
                 (Array.isArray(s.table_ids) && (s.table_ids.includes(table.id) || s.table_ids.includes(table.public_id)))
              );

              return (
                <Box key={table.id} sx={{ display: 'flex', borderBottom: '1px solid #e2e8f0', minHeight: 60 }}>
                  <Box sx={{ width: 200, minWidth: 200, maxWidth: 200, flexShrink: 0, p: 2, borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', justifyContent: 'center', overflow: 'hidden' }}>
                    <Typography noWrap sx={{ fontWeight: 700, color: '#334155', width: '100%' }}>{table.title || table.name}</Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>Capacidad: {table.seat_count || '--'} pax</Typography>
                  </Box>
                  <Box sx={{ flex: 1, position: 'relative', py: 1 }}>
                    {hourMarks.map((hm, i) => {
                       const left = (differenceInMinutes(hm, openDate) / totalMinutes) * 100;
                       if (left < 0 || left > 100) return null;
                       return <Box key={i} sx={{ position: 'absolute', left: `${left}%`, top: 0, bottom: 0, borderLeft: '1px solid #f1f5f9', height: '100%' }} />
                    })}

                    {tableEvents.map((evt, idx) => (
                      <Tooltip 
                        key={idx} 
                        title={<span style={{fontSize:'0.9rem'}}>{evt.customer_name} • {evt.guest_count} pax • {evt.customer_phone}</span>} 
                        arrow placement="top"
                      >
                        <Box sx={{ ...calculateBlockStyles(evt.start_time), bgcolor: '#22c55e' }} onClick={() => setSelectedReservation(evt)}>
                          {evt.customer_name}
                        </Box>
                      </Tooltip>
                    ))}
                  </Box>
                </Box>
              )
            })}
          </Box>
        )}
      </Paper>

      {/* Modal de Detalles de Reserva */}
      <Dialog open={Boolean(selectedReservation)} onClose={() => setSelectedReservation(null)} maxWidth="sm" fullWidth>
        {selectedReservation && (
          <>
            <DialogTitle sx={{ fontWeight: 800, color: '#0f172a', fontFamily: 'Como, sans-serif' }}>
              Detalles de la Reserva
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Cliente: <Box component="span" fontWeight="normal">{selectedReservation.customer_name}</Box>
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Teléfono: <Box component="span" fontWeight="normal">{selectedReservation.customer_phone || 'No registrado'}</Box>
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Personas: <Box component="span" fontWeight="normal">{selectedReservation.guest_count} pax</Box>
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Fecha y Hora: <Box component="span" fontWeight="normal">
                  {format(new Date(selectedReservation.start_time), "dd/MM/yyyy HH:mm", { locale: es })}
                </Box>
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Notas:
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-line', color: selectedReservation.notes ? 'text.primary' : 'text.secondary' }}>
                {selectedReservation.notes || 'Ninguna nota agregada.'}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedReservation(null)} sx={{ color: '#64748b', fontWeight: 'bold' }}>
                Cerrar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}

export default ReservationSchedule;
